import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { wallRowAt, wallTracks, wallWindow } from './wallWindow.mjs';

export function useWallWindow(slabs, focusIndex, onPosition) {
  const wall = useRef(null), grid = useRef(null);
  const [size, setSize] = useState({ columns: 3, tileWidth: 112, height: 600, gap: 22 });
  const [measurement, setMeasurement] = useState(null);
  const [top, setTop] = useState(0);
  const pendingShift = useRef(0), previous = useRef(null);
  const anchor = useRef(focusIndex);
  const known = useMemo(() => measurement?.slabs === slabs && measurement.columns === size.columns && measurement.tileWidth === size.tileWidth ? measurement.heights : new Map(), [measurement, slabs, size.columns, size.tileWidth]);
  const tracks = useMemo(() => wallTracks(slabs.length, size.columns, size.tileWidth / 0.6 + 140, known, size.gap), [slabs.length, size, known]);
  const range = wallWindow(tracks, top, size.height);

  useLayoutEffect(() => {
    const element = wall.current, shelves = grid.current;
    if (!element || !shelves) return undefined;
    function measure() {
      const style = getComputedStyle(shelves);
      // Explicit positions from the previous viewport can temporarily create
      // implicit CSS grid columns. Count from available width and the design's
      // minimum instead, so rotating from desktop to phone can shrink the grid.
      const columnGap = Number.parseFloat(style.columnGap) || 10;
      const minimum = Number.parseFloat(style.getPropertyValue('--sv-tile-min')) || 104;
      const padding = getComputedStyle(element);
      const width = element.clientWidth - Number.parseFloat(padding.paddingLeft) - Number.parseFloat(padding.paddingRight);
      const columns = Math.max(1, Math.floor((width + columnGap) / (minimum + columnGap)));
      const next = { columns, tileWidth: (width - (columns - 1) * columnGap) / columns, height: element.clientHeight, gap: Number.parseFloat(style.rowGap) || 22 };
      setSize(old => Object.keys(next).every(key => Math.abs(old[key] - next[key]) < 0.5) ? old : next);
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const element = wall.current, old = previous.current;
    // Keep the currently viewed shelf through viewport rotation. New filters
    // and returning from the showcase land on the selected slab.
    if (!old || old.slabs !== slabs || old.columns !== size.columns || old.tileWidth !== size.tileWidth) {
      // A shorter grid can clamp scrollTop during the DOM update, before this
      // effect. Retain the prior slab index instead of reading that clamped value.
      const nextAnchor = old?.slabs === slabs ? anchor.current : focusIndex;
      pendingShift.current = 0;
      element.scrollTop = tracks.offsets[Math.floor(nextAnchor / size.columns)] || 0;
      anchor.current = nextAnchor;
      setTop(element.scrollTop);
    } else if (pendingShift.current) {
      element.scrollTop += pendingShift.current;
      pendingShift.current = 0;
      setTop(element.scrollTop);
    }
    previous.current = { slabs, columns: size.columns, tileWidth: size.tileWidth, tracks };
  }, [slabs, size.columns, size.tileWidth, tracks, focusIndex]);

  useLayoutEffect(() => {
    const shelves = grid.current;
    if (!shelves) return undefined;
    const observer = new ResizeObserver(() => {
      const heights = new Map(known), visible = new Map();
      for (const tile of shelves.querySelectorAll('[data-wall-row] > button')) {
        const row = Number(tile.parentElement.dataset.wallRow);
        visible.set(row, Math.max(visible.get(row) || 0, tile.getBoundingClientRect().height));
      }
      let changed = false, shift = 0;
      const anchor = wallRowAt(tracks, wall.current.scrollTop);
      for (const [row, height] of visible) {
        if (height > 0 && Math.abs((heights.get(row) || tracks.heights[row]) - height) > 0.5) {
          if (row < anchor) shift += height - tracks.heights[row];
          heights.set(row, height);
          changed = true;
        }
      }
      if (changed) {
        pendingShift.current += shift;
        setMeasurement({ slabs, columns: size.columns, tileWidth: size.tileWidth, heights });
      }
    });
    for (const tile of shelves.querySelectorAll('[data-wall-row] > button')) observer.observe(tile);
    return () => observer.disconnect();
  }, [slabs, size.columns, size.tileWidth, known, tracks, range.start, range.end]);

  return { wall, grid, range, columns: size.columns, heights: tracks.heights, onScroll: event => {
    const offset = event.currentTarget.scrollTop;
    setTop(offset);
    const padding = getComputedStyle(event.currentTarget);
    const width = event.currentTarget.clientWidth - Number.parseFloat(padding.paddingLeft) - Number.parseFloat(padding.paddingRight);
    const columnGap = Number.parseFloat(getComputedStyle(grid.current).columnGap) || 10;
    // A resize may clamp the old scroll offset before its new tracks commit.
    // That is layout work, not a user choosing another slab.
    if (Math.abs((width - (size.columns - 1) * columnGap) / size.columns - size.tileWidth) > 0.5) return;
    anchor.current = Math.min(slabs.length - 1, wallRowAt(tracks, offset) * size.columns);
    onPosition?.(anchor.current);
  } };
}
